import { message, Spin, Table, Row, Col, Select, Form, Pagination } from "antd";
import Icon from "atoms/Icon";
import { CALL_API } from "common/API";
import COLORS from "common/Colors";
import { STATUS_CODE } from "common/Constants";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getUserInfo, getUserList } from "redux/selectors";
import styled from "styled-components";
import BillingInvoice from "./BillingInvoice";
import { uniqBy } from "lodash";
import { EditOutlined } from "@mui/icons-material";

const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
  }
  .ant-table-cell {
    padding: 20px;
  }
  .normal-column {
    min-width: 300px;
    white-space: normal;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
`;

const BillingApproval = ({ userInfo }) => {
  const iconStyle = { fontSize: "12px", cursor: "pointer" };
  const disabledIcon = {
    fontSize: "12px",
    cursor: "not-allowed",
    opacity: "0.3",
  };
  const history = useHistory();
  const [form] = Form.useForm();
  const containerRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const userList = useSelector(getUserList);
  const [showBillingInvoiceModal, setShowBillingInvoiceModal] = useState(false);
  const [billingInvoiceData, setBillingInvoiceData] = useState(null);
  const [invoiceMatterData, setInvoiceMatterData] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState({
    status: userInfo?.roleId === "9" ? ["Approved"] : ["Pending"],
    billingOffice: null,
    engagementName: null,
    clientname: null,
    submittedBy: null,
  });
  const limit = 20;
  const [page, setCurrentPage] = useState(1);
  const [billingDataCount, setBillingDataCount] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const isFinanceAdmin = userInfo?.roleId === "9";

  const handlePageChange = async (page, limit) => {
    setIsPageLoading(true);
    setCurrentPage(page);
  };

  const fetchMatterData = async (matterid) => {
    try {
      const res = await CALL_API(
        `insyts-matter/viewmatter?matterid=${matterid}`,
        "get"
      );
      return res?.response?.data || res?.data || res || null;
    } catch {
      return null;
    }
  };

  const showBillingInvoice = async (record) => {
    setShowBillingInvoiceModal(true);
    setBillingInvoiceData(record);
    if (record?.matterid) {
      const matterData = await fetchMatterData(record.matterid);
      setInvoiceMatterData(matterData);
    }
  };

  const { data: billingentity } = useFetch(queryKeys.getBillingEntityList.key, queryKeys.getBillingEntityList.url);

  const taxRate = billingInvoiceData?.statusname === "Pending" ? billingentity?.response?.data?.find(be => be.id === invoiceMatterData?.financial_resourcePlan?.billingEntityId)?.tax : billingInvoiceData?.tax || 0;
  
  const handleCloseInvoiceModal = () => {
    setShowBillingInvoiceModal(false);
  };

  const getCount = async () => {
    try {
      const billingStatus = form.getFieldValue("status") || ["Pending"];
      const filterStatus = Object.values(selectedFilters).some(
        (filter) => Array.isArray(filter) ? filter.length > 0 : filter !== null
      );
      const billingQueryParams = new URLSearchParams();
      // const isFinanceAdmin = userInfo?.roleId === "9";
      const appendQueryParams = (key, values) => {
        const arr = Array.isArray(values) ? values : values ? [values] : [];
        arr.forEach((value) => billingQueryParams.append(key, value));
      };

      appendQueryParams("statusname", billingStatus);
      appendQueryParams("billingoffice", billingOffice);
      appendQueryParams("mattername", engagementName);
      appendQueryParams("clientname", client);
      appendQueryParams("submittedby", submittedBy);
      if (isFinanceAdmin) {
        appendQueryParams("managingoffice", userInfo?.managingoffice?.[0]);
        billingQueryParams.append("financeadmin", "true");
      }
      billingQueryParams.append("filterStatus", filterStatus); // Add page to query params
      billingQueryParams.append("userId", userInfo?.userid);

      const billingQueryString = billingQueryParams.toString();

      const response = await CALL_API(
        `insyts/matter/billingapproval-count?${billingQueryString}`,
        "get"
      );
      setBillingDataCount(response);
    } catch (err) {
      console.error("Error fetching billing count:", err);
    }
  };

  useEffect(() => {
    getCount();
  }, [
    form.getFieldValue("status"),
    form.getFieldValue("billingOffice"),
    form.getFieldValue("engagementName"),
    form.getFieldValue("client"),
    form.getFieldValue("submittedBy")
  ]);

  const hasFilters = useMemo(() => {
    return Object.values(selectedFilters).some(
      (filter) => Array.isArray(filter) ? filter.length > 0 : filter !== null
    );
  }, [selectedFilters]);
  const billingStatus = form.getFieldValue("status") ? form.getFieldValue("status") : userInfo?.roleId === "9" ? ["Approved"] : ["Pending"];
  const billingOffice = form.getFieldValue("billingOffice") || [];
  const engagementName = form.getFieldValue("engagementName") || [];
  const client = form.getFieldValue("client") || [];
  const submittedBy = form.getFieldValue("submittedBy") || [];
  const approvalFilterQueryParams = new URLSearchParams();

  const appendApprovalFilterQueryParams = (key, values) => {
    const arr = Array.isArray(values) ? values : values ? [values] : [];
    arr.forEach((value) => approvalFilterQueryParams.append(key, value));
  };

  appendApprovalFilterQueryParams("statusname", billingStatus);
  approvalFilterQueryParams.append("userId", userInfo?.userid);
  appendApprovalFilterQueryParams("billingoffice", billingOffice);
  appendApprovalFilterQueryParams("mattername", engagementName);
  appendApprovalFilterQueryParams("clientname", client);
  appendApprovalFilterQueryParams("submittedby", submittedBy);
  if (isFinanceAdmin) {
    appendApprovalFilterQueryParams("managingoffice", userInfo?.managingoffice?.[0]);
    approvalFilterQueryParams.append("financeadmin", "true");
  }

  const approvalFilterQueryString = approvalFilterQueryParams.toString();

  const { key: billingDropdownKey, url: billingDropdownUrl } =
    queryKeys.getBillingFilterDropdown(`?${approvalFilterQueryString}`);

  const { data: billingDropdownDataRes, refetch: billingDropdownRefetch } = useFetch(
    billingDropdownKey,
    billingDropdownUrl
  );

  let billingFilterDropdownData = billingDropdownDataRes?.response?.data || [];

  const { getBillingApprovals } = queryKeys;

  const { key, url } = getBillingApprovals(
    page,
    limit,
    form.getFieldValue("status") || userInfo?.roleId === "9" ? ["Approved"] : ["Pending"],
    userInfo?.userid,
    isFinanceAdmin ? userInfo?.managingoffice?.[0] : undefined,
    isFinanceAdmin
  );
  const { data, isLoading, refetch } = useFetch(key, url, {
    enabled: !!userInfo?.userid,
  });
  useEffect(() => {
    const statusFilter = form.getFieldValue("status");
    if (!hasFilters) {
      setLoading(true);
      try {
        refetch({
          queryParams: {
            ...selectedFilters,
            statusname: statusFilter, // Explicitly include 'statusname'
          },
        });
      } finally {
        setLoading(false);
      }
    }
  }, [selectedFilters, page, limit]);

  const onFinish = async (record, type) => {
    try {
      setLoading(true);

      let matterData = invoiceMatterData;
    if (!matterData || matterData?.matterid !== record?.matterid) {
      matterData = await fetchMatterData(record.matterid);
    }

    const billingEntityId = parseInt(matterData?.financial_resourcePlan?.billingEntityId);
    const tax =
      record?.statusname === "Pending"
        ? billingentity?.response?.data?.find((be) => parseInt(be.id) === billingEntityId)?.tax
        : Number(record?.tax);

      const payload = {
        clientcode: record?.clientcode,
        matterid: record?.matterid,
        statusid: "1",
        statusname:
          type === "approved"
            ? "Approved"
            : "Declined",
        engagementPartner: record?.engagementPartner,
        billingoffice: record?.billingoffice,
        userid: record?.userid,
        billingdate: moment(record?.billingdate).format("YYYY-MM-DD"),
        internalbilling: record?.internalbilling,
        description: record?.description,
        currency: record?.currency,
        // subengagement: form?.subEngagement,
        amount: record?.amount,
        vat: record?.vat,
        financialyear: moment(record?.financialyear).format("YYYY-MM-DD"),
        loerefno: record?.loerefno,
        contactpersonid: record?.contactpersonid,
        contactperson: record?.contactperson,
        contactpersonacid: record?.contactpersonacid,
        contactpersonac: record?.contactpersonac,
        billingfilename: record?.billingfilename,
        tax: tax,
        content: "",
        billingfile: record?.billingfile,
        claims: record?.claims || [],
        timelog: record?.timelog || [],
      };
      const { code } = await CALL_API(
        `insyts/billing?matterid=${record?.matterid}&billingid=${record?.billingid}`,
        "patch",
        payload
      );
      setLoading(false);
      if (code === STATUS_CODE.SUCCESS) {
        message.success(
          type === "approved"
            ? "Approved Successfully"
            : "Declined Successfully"
        );
        billingRefetch();
        billingDropdownRefetch();
      }
    } catch (error) {
      message.error(
        "Something went wrong. Please reach out to our technical team."
      );
      setLoading(false);
      console.error("An error occurred:", error);
    }
  };

  const billingQueryParams = new URLSearchParams();

  const appendQueryParams = (key, values) => {
    const arr = Array.isArray(values) ? values : values ? [values] : [];
    arr.forEach((value) => billingQueryParams.append(key, value));
  };

  appendQueryParams("statusname", billingStatus);
  billingQueryParams.append("page", page); // Add page to query params
  billingQueryParams.append("limit", limit); // Add limit to query params
  billingQueryParams.append("userId", userInfo?.userid);
  appendQueryParams("billingoffice", billingOffice);
  appendQueryParams("mattername", engagementName);
  appendQueryParams("clientname", client);
  appendQueryParams("submittedby", submittedBy);
  if (isFinanceAdmin) {
    appendQueryParams("managingoffice", userInfo?.managingoffice?.[0]);
    billingQueryParams.append("financeadmin", true);
  }


  const billingQueryString = billingQueryParams.toString();

  const { key: billingFilterKey, url: billingFilterUrl } =
    queryKeys.getBillingFilter(`?${billingQueryString}`);

  const {
    data: fetchedData,
    refetch: billingRefetch,
  } = useFetch(billingFilterKey, billingFilterUrl, {
    enabled: billingQueryString.length > 0,
  });
  useEffect(() => {
    const fetchPageData = async () => {
      setIsPageLoading(true);
      if (billingQueryString && hasFilters) {
        await billingRefetch();
        await billingDropdownRefetch();
      }
      setIsPageLoading(false);
    };
    fetchPageData();
  }, [page]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Matter",
      dataIndex: "mattername",
      key: "mattername",
      sorter: (a, b) => a.mattername.localeCompare(b.mattername)
    },
    {
      title: "Client",
      dataIndex: "clientname",
      key: "clientname",
      sorter: (a, b) => a.clientname.localeCompare(b.clientname),
    },
    {
      title: "Billing Office",
      dataIndex: "billingoffice",
      key: "billingoffice",
      sorter: (a, b) => a.billingoffice.localeCompare(b.billingoffice)
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      sorter: (a, b) => a.currency.localeCompare(b.currency)
    },
    // {
    //   title: "Internal Billing",
    //   dataIndex: "internalbilling",
    //   key: "internalbilling",
    //   sorter: (a, b) => Number(a.internalbilling) - Number(b.internalbilling),
    //   render: (internalbilling) => (internalbilling ? "Yes" : "No"),
    // },
    {
      title: "Status",
      dataIndex: "statusname",
      key: "statusname",
      sorter: (a, b) => a.statusname.localeCompare(b.statusname)
    },
    {
      title: "Approved Declined Date",
      dataIndex: "billingstatusdate",
      key: "approvedDate",
      sorter: (a, b) => {
        const statusDate = {
          "Approved": a.billingapproveddate,
          "Declined": a.billingdeclineddate,
        };

        const statusDateB = {
          "Approved": b.billingapproveddate,
          "Declined": b.billingdeclineddate,
        };

        const dateA = statusDate[a.statusname] ? new Date(statusDate[a.statusname]) : null;
        const dateB = statusDateB[b.statusname] ? new Date(statusDateB[b.statusname]) : null;

        if (!dateA && !dateB) return 0;
        if (!dateA) return -1;
        if (!dateB) return 1;
        return dateA - dateB;
      },
      render: (text, record) => {
        const statusDate = {
          "Approved": record.billingapproveddate,
          "Declined": record.billingdeclineddate,
        };

        const date = statusDate[record.statusname];

        return date ? moment(date).format("DD-MMM-YYYY") : "-";
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
      render: (amount) =>
        Number(amount).toFixed(2),
    },
    {
      title: "Date Submitted",
      dataIndex: "createdat",
      key: "createdat",
      sorter: (a, b) => new Date(a.createdat) - new Date(b.createdat),
      render: (createdat) => moment(createdat).format("DD-MMM-YYYY") || '-',
    },
    {
      title: "Submitted By",
      dataIndex: "userid",
      key: "userid",
      sorter: (a, b) => {
        const nameA = userList?.find((item) => item?.userid === a?.userid)?.fullname || "";
        const nameB = userList?.find((item) => item?.userid === b?.userid)?.fullname || "";
        return nameA?.localeCompare(nameB);
      },
      render: (val) => {
        const data = userList?.find((item) => item?.userid === val);
        return data?.fullname || val;
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "id",
      render: (text, record) => {
        const isFinanceRole = userInfo?.roleId === "9";
        const isInternalBilling = record?.internalbilling === true;

        const isApprovedOrDeclined =
          // 1️⃣ Finance already acted → always lock
          record?.statusname === "Approved" ||

          // 2️⃣ Pending → disabled only for Finance
          (isFinanceRole && record?.statusname === "Pending");

        return (
          <div className="d-flex gap-2">
            {/* ✅ Approve */}
            <Icon
              name="tick"
              style={isApprovedOrDeclined ? disabledIcon : iconStyle}
              onClick={(e) => {
                if (isApprovedOrDeclined) return;
                e.stopPropagation();
                onFinish(record, "approved");
              }}
            />

            {/* ❌ Decline */}
            <Icon
              name="wrong"
              style={isApprovedOrDeclined ? disabledIcon : iconStyle}
              onClick={(e) => {
                if (isApprovedOrDeclined) return;
                e.stopPropagation();
                onFinish(record, "declined");
              }}
            />

            {/* ✏️ Edit – still restricted for Finance */}
            {record?.statusname === "Pending" &&
              userInfo?.roleId !== "9" && (
                <div
                  // style={{
                  //   ...iconStyle,
                  //   backgroundColor: "#C2FAE5",
                  //   height: "30px",
                  //   width: "32px",
                  //   borderRadius: "4px",
                  //   display: "flex",
                  //   justifyContent: "center",
                  //   alignItems: "center",
                  // }}
                >
                  <EditOutlined
                   style={{
                color: "#00685F",
                cursor: "pointer",
                background: "#deefee",
                padding: "2px",
                borderRadius: "4px",
                height: "28px",
                width: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12
              }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const matterData =
                        invoiceMatterData?.matterid === record.matterid
                          ? invoiceMatterData
                          : await fetchMatterData(record.matterid);

                      history.push(`/approvals/bills/${record.matterid}`, {
                        record,
                        viewbillings: false,
                        tabid: "billing_approval",
                        matterId: record.matterid,
                        matterData,
                      });
                    }}
                  />
                </div>
              )}

            {/* 👁 View */}
            <Icon
              name="eye"
              style={iconStyle}
              onClick={() => {
                showBillingInvoice(record);
              }}
            />
          </div>
        );
      },
    }

  ];

  const ApprovedTotalInternalBillingAmount = (form.getFieldValue("status") === "Approved") ? billingDataCount?.totalinternalamount : 0;

  const ApprovedTotalExternalBillingAmount = (form.getFieldValue("status") === "Approved") ? billingDataCount?.totalexternalamount : 0;

  const PendingTotalInternalBillingAmount = form.getFieldValue("status") === "Pending" ? billingDataCount?.totalinternalamount : 0;

  const PendingTotalExternalBillingAmount = form.getFieldValue("status") === "Pending" ? billingDataCount?.totalexternalamount : 0;

  const statusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Approved", value: "Approved" },
    { label: "Declined", value: "Declined" },
  ];

  // Function to handle filter changes
  const handleFilterChange = (changedValues) => {
    setLoading(true);
    setSelectedFilters((prev) => ({
      ...prev,
      ...changedValues,
    }));
    setCurrentPage(1);
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (billingQueryString && hasFilters) {
        await billingRefetch();
        await billingDropdownRefetch();
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedFilters, page]);

  return (
    <>
      <div ref={containerRef}>
        <Form
          form={form}
          onValuesChange={(changedValues) => handleFilterChange(changedValues)}
          initialValues={{
            ...selectedFilters,
            status: selectedFilters.status, // Ensure default value
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}>
              <Form.Item name="status" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Status"
                  selectedValues={selectedFilters.status}
                  onChange={(value) => handleFilterChange({ status: value })}
                  getPopupContainer={() => containerRef.current}
                >
                  {uniqBy(
                    statusOptions?.map((item) => ({
                      label: item?.label,
                      value: item?.value,
                    })),
                    "label"
                  )?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item name="engagementName" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Matter Title"
                  selectedValues={selectedFilters.engagementName}
                  onChange={(value) =>
                    handleFilterChange({ engagementName: value })
                  }
                  allowClear
                  getPopupContainer={() => containerRef.current}
                >
                  {uniqBy(
                    billingFilterDropdownData?.map((item) => {
                      return {
                        label: item?.mattername,
                        value: item?.mattername,
                      };
                    }),
                    "label"
                  )?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item name="client" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Client"
                  selectedValues={selectedFilters.clientname}
                  onChange={(value) => handleFilterChange({ clientname: value })}
                  allowClear
                  getPopupContainer={() => containerRef.current}
                >
                  {uniqBy(
                    billingFilterDropdownData?.map((item) => {
                      return {
                        label: item?.clientname,
                        value: item?.clientname,
                      };
                    }),
                    "label"
                  )?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item name="billingOffice" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Billing Office"
                  selectedValues={selectedFilters.billingOffice}
                  onChange={(value) =>
                    handleFilterChange({ billingOffice: value })
                  }
                  allowClear
                  getPopupContainer={() => containerRef.current}
                >
                  {uniqBy(
                    billingFilterDropdownData?.map((item) => {
                      return {
                        label: item?.billingoffice,
                        value: item?.billingoffice,
                      };
                    }),
                    "label"
                  )?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item name="submittedBy" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Submitted By"
                  value={selectedFilters.submittedBy}
                  onChange={(value) => handleFilterChange({ submittedBy: value })}
                  allowClear
                  getPopupContainer={() => containerRef.current}
                >
                  {uniqBy(
                    billingFilterDropdownData?.map((item) => {
                      const user = userList?.find((u) => u?.userid === item?.userid);
                      return {
                        label: user?.fullname || item?.userid,
                        value: item?.userid,
                      };
                    }),
                    "value"
                  )?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>

      <Spin spinning={isLoading || loading || isPageLoading}>
        <StyledTable
          columns={columns}
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          rowKey="id"
          dataSource={hasFilters ? fetchedData?.response?.data : data?.response?.data}
          pagination={false}
          onRow={(record, index) => ({
            onClick: (event) => {
              showBillingInvoice(record);
            },
          })}
        />

        {((hasFilters ? fetchedData?.response?.data : data?.response?.data)?.length > 0) && (
          <div className="pagination-container">
            <Pagination
              current={page}
              pageSize={20}
              total={billingDataCount?.total || 0}
              onChange={handlePageChange}
              style={{ marginTop: "16px", textAlign: "right" }}
              showSizeChanger={false}
            />
          </div>
        )}

        <div className="w-100 d-flex justify-content-end mt-5">
          <div>
            <h6>
              Approved (Internal) Total Billing amount:{" "}
              {ApprovedTotalInternalBillingAmount
                ? Number(ApprovedTotalInternalBillingAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : "0.00"}
            </h6>
            <h6>
              Approved (External) Total Billing amount:{" "}
              {ApprovedTotalExternalBillingAmount
                ? Number(ApprovedTotalExternalBillingAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : "0.00"}
            </h6>
            <h6>
              Pending (Internal) Total Billing amount:{" "}
              {PendingTotalInternalBillingAmount
                ? Number(PendingTotalInternalBillingAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : "0.00"}
            </h6>
            <h6>
              Pending (External) Total Billing amount:{" "}
              {PendingTotalExternalBillingAmount
                ? Number(PendingTotalExternalBillingAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : "0.00"}
            </h6>
          </div>
        </div>

        <BillingInvoice
          billingInvoiceData={billingInvoiceData}
          open={showBillingInvoiceModal}
          handleClose={handleCloseInvoiceModal}
          matterData={invoiceMatterData}
          taxRate={taxRate}
        />
      </Spin>
    </>
  );
};
const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(BillingApproval);
